import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList,
  Renderer2, SimpleChanges, ViewChild, ViewChildren
} from '@angular/core';
import { NpaAction, NpaActionConfig } from './table-actions.types';
import { BoxModelService, IconsOld, NpaElement, NpaId, NpaOption, ToolService } from '@circe/core';
import { NpaActionConfigSource, NpaTableActionType, NpaTableColumn, NpaTableRowValue } from '../table.types';
import { Subject } from 'rxjs';
import { NpaDropdownConfig } from '@circe/dropdown';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'npa-table-actions',
  templateUrl: 'table-actions.component.html',
  styleUrls: ['table-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableActionsComponent<T> implements OnInit, OnChanges {
  @ViewChild('actionsContainer') actionsContainer: ElementRef;
  @ViewChild('dropdownTrigger') dropdownTrigger: ElementRef;
  @ViewChildren('actionElement') actionElements: QueryList<ElementRef>;
  @Input() actions: NpaActionConfig<T>[] | NpaActionConfigSource<T>;
  @Input() actionsType: NpaTableActionType;
  @Input() setDropdownParentTo: NpaElement;
  @Input() dropdownConfig: NpaDropdownConfig;
  @Input() tableRow: NpaTableColumn<T>[];
  @Input() rowData: NpaTableRowValue<T>;
  @Input() isHoveringRow: boolean;
  @Input() hotRender: boolean;
  @Input() group: boolean;
  @Input() defaultActionId: NpaId;
  @Input() actionsDropdownRelativeElement: NpaElement;
  @Input() manualHideDropdown: Subject<void> = new Subject<void>();

  @Output() selectedOption: EventEmitter<NpaAction<T>> = new EventEmitter<NpaAction<T>>();
  @Output() defaultAction: EventEmitter<NpaAction<T>> = new EventEmitter<NpaAction<T>>();

  public viewActions: NpaAction<T>[] = [];
  public processedActions: NpaAction<T>[] = [];
  public internalId: string = ToolService.generateUuid();
  public showDropdown: boolean;
  public dropdownItems: NpaOption[] = [];
  public processedDropdownConfig: NpaDropdownConfig;
  public dropdownKeyboard$: Subject<KeyboardEvent> = new Subject();

  private _iconHandler: IconsOld = new IconsOld();
  private _componentDestroyed$: Subject<void> = new Subject<void>();

  constructor(
    public el: ElementRef<HTMLElement>,
    public tools: ToolService,
    private _cd: ChangeDetectorRef,
    private _renderer: Renderer2,
    private _bm: BoxModelService
  ) {}

  private _processConfigurations(): void {
    const actionsConfigurations: NpaActionConfig<T>[] = (typeof this.actions === 'function' ?
      (this.actions as NpaActionConfigSource<T>)(this.rowData) : this.actions) as NpaActionConfig<T>[];
    if (!!actionsConfigurations?.length) {
      this.processedActions = actionsConfigurations.map(this._mapActionConfigToAction.bind(this));

      const _defaultAction: NpaActionConfig<T> = actionsConfigurations.find((a: NpaActionConfig<T>) => a.value === this.defaultActionId);
      if (_defaultAction) {
        this.defaultAction.emit(_defaultAction);
      }
    }
    this.dropdownItems = [...this.processedActions];
    this.viewActions = [...this.processedActions];
  }

  private _mapActionConfigToAction(actionConfiguration: NpaActionConfig<T>): NpaAction<T> {
    const _iconHandler: IconsOld = this._iconHandler;
    const _result: NpaAction<T> = { ...actionConfiguration };

    if ('icon' in actionConfiguration) {
      if ('iconLeft' in actionConfiguration.icon) {
        _result.iconLeft = _iconHandler.getIcon(actionConfiguration.icon.iconLeft);
      }
      if ('iconRight' in actionConfiguration.icon) {
        _result.iconRight = _iconHandler.getIcon(actionConfiguration.icon.iconRight);
      }
    }

    _result.callback = () => {};
    if ('callback' in actionConfiguration) {
      _result.callback = actionConfiguration.callback;
    }

    return _result;
  }

  public ngOnInit(): void {
    this.processedDropdownConfig = {
      elementReference: { type: 'id', name: 'npa-table-actions__dropdown-trigger-' + this.internalId },
      elementRelative: 'cdk-virtual-scroll-content-wrapper',
      elementBoundary: 'npa-table__body',
      position: 'bottom right',
      orientation: 'bottom left',
      clickOutsideApply: true,
    };
    if (this.dropdownConfig) {
      this.processedDropdownConfig = {
        ...this.processedDropdownConfig,
        ...this.dropdownConfig,
        elementReference: this.processedDropdownConfig.elementReference
      };
    }
    this._processConfigurations();

    if (this.manualHideDropdown) {
      this.manualHideDropdown
        .pipe(takeUntil(this._componentDestroyed$))
        .subscribe(() => {
          this.showDropdown = false;
          this._cd.markForCheck();
        });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.hotRender && changes.actions?.currentValue) {
      this._processConfigurations();
    }
  }

  public dropdownSelected(option: NpaOption): void {
    this.selectedOption.emit(this.processedActions.find((a: NpaAction<T>) => a.value === option.value));
  }

  public onDropdownShow(): void {
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown && this.setDropdownParentTo) {
      setTimeout(() => {
        const _dropdownParent: Element = this._bm.getElement(this.setDropdownParentTo);
        const _dropdown = this.el.nativeElement.getElementsByTagName('npa-dropdown').item(0);
        if (_dropdown && _dropdownParent) {
          this._renderer.appendChild(_dropdownParent, _dropdown);
        }
      });
    }
  }
}
